import React from "react";
import { Link, Typography } from "@mui/material";
import styled from "@emotion/styled";
import LegendSwatch from "@hyro-components/hyro-pie-chart/LegendSwatch";
import TrendIndicator from "src/components/widgets/overview/tiles/TrendIndicator";
import HyroTooltipSegmentedBar from "./HyroTooltipSegmentedBar";
import { TOOLTIP_SECTION_LABEL_MAX_WIDTH, TOOLTIP_SECTION_ROW_LINK_TEST_ID } from "./HyroTooltipSection.consts";

// rows: [{ label, value, href?, delta?, color?, sublabel?, valueColor? }], segments?: [{ key, value, color }] drawn as a share
// bar under the title. Rendered as a fragment so the parent's column gap spaces the divider and section like
// any other tooltip line.
const HyroTooltipSection = ({ title, rows = [], divider = true, segments, valueColor = "text.700" }) => (
  <>
    {divider && <Divider />}
    <Section>
      {title && <Typography variant="tooltip" color="text.600">{title}</Typography>}
      {segments && <HyroTooltipSegmentedBar segments={segments} />}
      {rows.map((row, rowIndex) => (
        // keyed by position, not label — row labels can repeat (two agents may share a name)
        <SectionRow key={`${title}-${rowIndex}`} alignToBottom={Boolean(row.sublabel)}>
          <SectionLabelGroup>
            {row.color && <LegendSwatch color={row.color} />}
            <SectionLabelStack>
              {row.href ? (
                <SectionLabelLink
                  data-testid={TOOLTIP_SECTION_ROW_LINK_TEST_ID}
                  variant="tooltip"
                  color="text.700"
                  href={row.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                >
                  {row.label}
                </SectionLabelLink>
              ) : (
                <SectionLabel variant="tooltip" color="text.700">{row.label}</SectionLabel>
              )}
              {row.sublabel && <Typography variant="tooltip" color="text.600">{row.sublabel}</Typography>}
            </SectionLabelStack>
          </SectionLabelGroup>
          <SectionValueGroup>
            <Typography variant="tooltip" color={row.valueColor ?? valueColor}>{row.value}</Typography>
            {row.delta && <TrendIndicator {...row.delta} showPeriod={false} />}
          </SectionValueGroup>
        </SectionRow>
      ))}
    </Section>
  </>
);

const Divider = styled("div")(({ theme }) => ({
  height: 1,
  width: "100%",
  backgroundColor: theme.palette.system[200]
}));

const Section = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1)
}));

const SectionRow = styled("div", { shouldForwardProp: (prop) => prop !== "alignToBottom" })(({ theme, alignToBottom }) => ({
  display: "flex",
  alignItems: alignToBottom ? "flex-end" : "center",
  justifyContent: "space-between",
  gap: theme.spacing(2)
}));

const SectionLabelGroup = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  minWidth: 0
}));

const SectionLabelStack = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  minWidth: 0
});

// caps only the label, so a long row label (e.g. an agent name) wraps instead of stretching the
// whole tooltip. anywhere lets a single over-long token break too. Labels below the cap are unaffected.
const SectionLabel = styled(Typography)({
  maxWidth: TOOLTIP_SECTION_LABEL_MAX_WIDTH,
  overflowWrap: "anywhere"
});

const SectionLabelLink = styled(Link)({
  maxWidth: TOOLTIP_SECTION_LABEL_MAX_WIDTH,
  overflowWrap: "anywhere"
});

const SectionValueGroup = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  flexShrink: 0,
  whiteSpace: "nowrap"
}));

export default HyroTooltipSection;
